import { useCallback, useMemo, type ReactNode } from 'react';
import { useCanAccess } from '@permissions/useFeatures';
import { useAreaReview } from '../../SchedulerPage/components/AreaReview/useAreaReview';
import { areaReportTier } from '../../SchedulerPage/lib/locationStatusLabels';
import type { IMatrixColumn } from './progressMatrix';
import type { IAreaEditor } from './matrixCellPropsEqual';
import { useTrackerAreaWrite } from './useTrackerAreaWrite';
import { trackerWriteFailure, type ITrackerWriteFailure } from './trackerWriteFailure';

/**
 * How this user edits the tracker's area cells (area review stage spec,
 * rulings 3 and 7): a Limited user (`tasks.contribute`) submits areas for
 * review, a manager (`tasks.execute`) reports them done and reviews
 * submissions, anyone else only reads. `review` is the open review dialog, if any.
 */
export function useTrackerAreaEditor(
  siteId: string,
  scheduleNumber: string | undefined,
  columns: readonly IMatrixColumn[],
  /** A cell write didn't land and the cell was re-read — tell the user, in
   *  the copy the failure calls for. */
  onWriteFailed: (copy: ITrackerWriteFailure) => void,
): { areaEditor: IAreaEditor; review: ReactNode } {
  const onFailed = useCallback((error: unknown) => onWriteFailed(trackerWriteFailure(error)), [onWriteFailed]);
  const write = useTrackerAreaWrite(siteId, scheduleNumber, onFailed);
  const tier = areaReportTier(useCanAccess('tasks.execute'), useCanAccess('tasks.contribute'));

  // The reject's issue carries the task's profession; the columns know it.
  const professionByTask = useMemo(() => new Map(columns.map((c) => [c.taskId, c.profession])), [columns]);
  const { open, dialog } = useAreaReview(siteId, (id) => ({ id, profession: professionByTask.get(id) }), write);

  // Off the columns: a column rebuild must not re-render every memoised cell.
  const areaEditor = useMemo<IAreaEditor>(() => ({ tier, write, review: open }), [tier, write, open]);
  return { areaEditor, review: dialog };
}
